/**
 * Supplier Selector Component
 * Provides autofill functionality for supplier selection in quote management
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Button,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';
import { supplierService, Supplier } from '../../../services/supplierService';
import SupplierManagementDialog from './SupplierManagementDialog';

interface SupplierSelectorProps {
  onSupplierSelected: (supplier: Supplier) => void;
  selectedSupplier?: Supplier | null;
  defaultEmail?: string;
  showAddButton?: boolean;
  label?: string;
  required?: boolean;
}

const SupplierSelector: React.FC<SupplierSelectorProps> = ({
  onSupplierSelected,
  selectedSupplier = null,
  defaultEmail = '',
  showAddButton = true,
  label = 'Lieferant auswählen',
  required = false
}) => {
  const { showNotification } = useNotification();
  
  // State
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSupplierDialog, setShowSupplierDialog] = useState(false);

  // Load suppliers on mount
  useEffect(() => {
    loadSuppliers();
  }, []);

  // Auto-select supplier if email matches
  useEffect(() => {
    if (defaultEmail && suppliers.length > 0 && !selectedSupplier) {
      const matchingSupplier = suppliers.find(s => 
        s.email.toLowerCase() === defaultEmail.toLowerCase()
      );
      if (matchingSupplier) {
        onSupplierSelected(matchingSupplier);
        showNotification(`Lieferant automatisch gefunden: ${matchingSupplier.name}`, 'info');
      }
    }
  }, [defaultEmail, suppliers, selectedSupplier, onSupplierSelected, showNotification]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const response = await supplierService.getSuppliers();
      if (response.success) {
        setSuppliers(response.data || []);
      } else {
        showNotification('Fehler beim Laden der Lieferanten: ' + response.message, 'error');
      }
    } catch (error: any) {
      showNotification('Fehler beim Laden der Lieferanten: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierSelect = (supplier: Supplier | null) => {
    if (supplier) {
      onSupplierSelected(supplier);
      showNotification(`Lieferant ausgewählt: ${supplier.name}`, 'success');
    }
  };

  const handleSupplierAdded = (newSupplier: Supplier) => {
    // Refresh supplier list
    loadSuppliers();
    // Auto-select the new supplier
    onSupplierSelected(newSupplier);
    setShowSupplierDialog(false);
  };

  const getSupplierOptionLabel = (supplier: Supplier) => {
    return `${supplier.name} (${supplier.company_name}) - ${supplier.email || 'Keine E-Mail'}`;
  };

  const getSupplierDisplayText = (supplier: Supplier) => {
    return supplier.company_name || supplier.name;
  };

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    supplier.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    supplier.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={showAddButton ? 10 : 12}>
          <Autocomplete
            value={selectedSupplier}
            onChange={(_, newValue) => handleSupplierSelect(newValue)}
            options={filteredSuppliers}
            getOptionLabel={getSupplierOptionLabel}
            loading={loading}
            filterOptions={(options) => options} // Use our own filtering
            onInputChange={(_, newInputValue) => setSearchQuery(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label + (required ? ' *' : '')}
                placeholder="Lieferant suchen oder auswählen..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                helperText={
                  defaultEmail && !selectedSupplier 
                    ? `Automatische Suche für: ${defaultEmail}` 
                    : selectedSupplier 
                    ? `Ausgewählt: ${getSupplierDisplayText(selectedSupplier)}`
                    : 'Geben Sie Text ein, um Lieferanten zu suchen'
                }
              />
            )}
            renderOption={(props, supplier) => (
              <Box component="li" {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {supplier.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {supplier.company_name} - {supplier.email || 'Keine E-Mail'}
                    </Typography>
                    {supplier.phone && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Tel: {supplier.phone}
                      </Typography>
                    )}
                  </Box>
                  <Chip label={supplier.id} size="small" />
                </Box>
              </Box>
            )}
            noOptionsText={
              searchQuery.length > 0 
                ? `Keine Lieferanten gefunden für "${searchQuery}"`
                : "Geben Sie Text ein, um zu suchen"
            }
          />
        </Grid>
        
        {showAddButton && (
          <Grid item xs={2}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowSupplierDialog(true)}
              fullWidth
              sx={{ height: '56px' }} // Match TextField height
            >
              Neu
            </Button>
          </Grid>
        )}
      </Grid>

      {selectedSupplier && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success" sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2">
                Ausgewählter Lieferant: {selectedSupplier.name}
              </Typography>
              <Typography variant="body2">
                {selectedSupplier.company_name} - {selectedSupplier.email || 'Keine E-Mail'}
              </Typography>
              {selectedSupplier.phone && (
                <Typography variant="body2">
                  Tel: {selectedSupplier.phone}
                </Typography>
              )}
            </Box>
          </Alert>
        </Box>
      )}

      {/* Supplier Management Dialog */}
      <SupplierManagementDialog
        open={showSupplierDialog}
        onClose={() => setShowSupplierDialog(false)}
        emailContext={{
          email_id: '',
          from_address: defaultEmail || '',
          subject: '',
          body_text: ''
        }}
        onSupplierSelected={handleSupplierAdded}
      />
    </Box>
  );
};

export default SupplierSelector;
